import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, limit, startAfter, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useCategories } from './useCategories';

export interface POSProduct {
  id: string;
  code: string;
  name: string;
  netPrice: number;
  taxRate: string;
  unit: string;
  category?: {
    categoryId?: string;
    subCategoryId?: string;
    id?: string;
    name?: string;
  };
  isActive: boolean;
  isService: boolean;
  discontinued: boolean;
  type: string;
  thumbnailUrl?: string;
}

interface POSFilters {
  searchTerm?: string;
  categoryId?: string;
  hasStock?: boolean; // van-e készlet az aktuális raktárban
  hasAnyStock?: boolean; // van-e készlet bárhol
  isService?: boolean;
  productType?: string;
  isActive?: boolean; // aktív-e a termék
}

export interface ProductStock {
  [productId: string]: {
    [warehouseId: string]: {
      quantity: number;
      reservedQuantity: number;
      availableQuantity: number;
    };
  };
}

interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export const usePOSProducts = (
  filters: POSFilters = {}, 
  selectedWarehouseId?: string,
  currentPage = 1,
  pageSize = 50
) => {
  const [products, setProducts] = useState<POSProduct[]>([]);
  const [allProducts, setAllProducts] = useState<POSProduct[]>([]);
  const [stockData, setStockData] = useState<ProductStock>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    pageSize: 50,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  // Használjuk a meglévő kategória hook-ot
  const { categories, loading: categoriesLoading } = useCategories();

  // Készlet adatok betöltése Firebase-ből
  const fetchInventoryData = async () => {
    try {
      console.log('📦 Készlet adatok betöltése Firebase-ből...');
      const inventoryRef = collection(db, 'inventory');
      const inventoryQuery = query(inventoryRef);
      
      const inventorySnapshot = await getDocs(inventoryQuery);
      const stockInfo: ProductStock = {};
      
      inventorySnapshot.forEach((doc) => {
        const data = doc.data();
        const productId = data.productId;
        const warehouseId = data.warehouseId;
        
        if (!stockInfo[productId]) {
          stockInfo[productId] = {};
        }
        
        stockInfo[productId][warehouseId] = {
          quantity: data.quantity || 0,
          reservedQuantity: data.reservedQuantity || 0,
          availableQuantity: Math.max(0, (data.quantity || 0) - (data.reservedQuantity || 0))
        };
      });
      
      console.log(`📊 Készlet adatok betöltve: ${Object.keys(stockInfo).length} termékhez`);
      setStockData(stockInfo);
    } catch (err) {
      console.error('❌ Hiba a készlet adatok betöltésekor:', err);
    }
  };

  const fetchProducts = async (loadAll = false) => {
    try {
      setLoading(true);
      
      if (loadAll) {
        console.log('🔄 Összes termék betöltése szűréshez...');
        
        // Összes termék betöltése (aktív és inaktív is) - csak a töröltek maradnak ki
        const productsQuery = query(
          collection(db, 'products'),
          orderBy('name')
        );

        const querySnapshot = await getDocs(productsQuery);
        const fetchedProducts: POSProduct[] = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Csak a teljesen töröltek/megszüntetettek maradnak ki
          // Az inaktív termékek is megjelennek, de jelölve lesznek
          if (data.discontinued === true) return;
          
          const product = {
            id: doc.id,
            code: data.code || '',
            name: data.name || '',
            netPrice: data.netPrice || 0,
            taxRate: data.taxRate || '27%',
            unit: data.unit || 'db',
            category: data.category,
            isActive: data.isActive !== false, // alapértelmezetten aktív, ha nincs megadva
            isService: data.isService || false,
            discontinued: data.discontinued || false,
            type: data.type || 'product',
            thumbnailUrl: data.thumbnailUrl
          };

          fetchedProducts.push(product);
        });

        console.log(`✅ Összes termék betöltve: ${fetchedProducts.length} termék (aktív és inaktív is)`);
        setAllProducts(fetchedProducts);
      } else {
        console.log(`🔄 Termékek betöltése - ${currentPage}. oldal (${pageSize} termék/oldal)...`);
        
        // Paginált termékek betöltése (minden termék)
        const productsQuery = query(
          collection(db, 'products'),
          orderBy('name'),
          limit(pageSize)
        );

        const querySnapshot = await getDocs(productsQuery);
        const fetchedProducts: POSProduct[] = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          if (data.discontinued === true) return;
          
          const product = {
            id: doc.id,
            code: data.code || '',
            name: data.name || '',
            netPrice: data.netPrice || 0,
            taxRate: data.taxRate || '27%',
            unit: data.unit || 'db',
            category: data.category,
            isActive: data.isActive !== false,
            isService: data.isService || false,
            discontinued: data.discontinued || false,
            type: data.type || 'product',
            thumbnailUrl: data.thumbnailUrl
          };

          fetchedProducts.push(product);
        });

        console.log(`✅ ${currentPage}. oldal betöltve: ${fetchedProducts.length} termék`);
        setProducts(fetchedProducts);
      }
      
      setError(null);
    } catch (err) {
      console.error('❌ Hiba a termékek betöltése során:', err);
      setError('Nem sikerült betölteni a termékeket');
    } finally {
      setLoading(false);
    }
  };

  // Szűrés alkalmazása (kliens oldalon az összes termékből)
  const applyFilters = (productsToFilter: POSProduct[]) => {
    return productsToFilter.filter(product => {
      const stockInfo = getProductStock(product.id);

      // Szöveg keresés
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const matchesSearch = 
          product.name.toLowerCase().includes(term) ||
          product.code.toLowerCase().includes(term);
        if (!matchesSearch) return false;
      }

      // Kategória szűrés
      if (filters.categoryId) {
        const matchesCategory = 
          product.category?.categoryId === filters.categoryId ||
          product.category?.subCategoryId === filters.categoryId;
        if (!matchesCategory) return false;
      }

      // Készlet szűrés (aktuális raktár)
      if (filters.hasStock !== undefined) {
        if (filters.hasStock && stockInfo.currentWarehouseStock === 0) return false;
        if (!filters.hasStock && stockInfo.currentWarehouseStock > 0) return false;
      }

      // Készlet szűrés (bármely raktár)
      if (filters.hasAnyStock !== undefined) {
        if (filters.hasAnyStock && stockInfo.totalStock === 0) return false;
        if (!filters.hasAnyStock && stockInfo.totalStock > 0) return false;
      }

      // Szolgáltatás/termék szűrés
      if (filters.isService !== undefined && product.isService !== filters.isService) {
        return false;
      }

      // Termék típus szűrés
      if (filters.productType && product.type !== filters.productType) {
        return false;
      }

      // Aktív termék szűrés
      if (filters.isActive !== undefined && product.isActive !== filters.isActive) {
        return false;
      }

      return true;
    });
  };

  // Paginációs logika
  const applyPagination = (filteredProducts: POSProduct[]) => {
    const totalItems = filteredProducts.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

    setPagination({
      currentPage,
      pageSize,
      totalItems,
      totalPages,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1
    });

    return paginatedProducts;
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchProducts(true), // Összes termék betöltése szűréshez
        fetchInventoryData()
      ]);
    };
    
    loadData();
  }, []);

  // Szűrők vagy pagináció változásakor frissítés
  useEffect(() => {
    if (allProducts.length > 0) {
      const filtered = applyFilters(allProducts);
      const paginated = applyPagination(filtered);
      setProducts(paginated);
    }
  }, [filters, currentPage, pageSize, allProducts, stockData, selectedWarehouseId]);

  // Készlet információ lekérése termékhez
  const getProductStock = (productId: string) => {
    const productStock = stockData[productId] || {};
    const currentWarehouseStock = selectedWarehouseId && productStock[selectedWarehouseId] 
      ? productStock[selectedWarehouseId].availableQuantity 
      : 0;
    
    const stockSources = Object.entries(productStock)
      .filter(([warehouseId, stock]) => stock.availableQuantity > 0)
      .map(([warehouseId, stock]) => ({
        warehouseId,
        availableQuantity: stock.availableQuantity
      }));

    return {
      currentWarehouseStock,
      stockSources,
      totalStock: Object.values(productStock).reduce((sum, stock) => sum + stock.availableQuantity, 0)
    };
  };

  // Kategóriák feldolgozása POS számára
  const posCategories = categories.map(category => ({
    id: category.id,
    name: category.name
  }));

  return {
    products,
    allProducts,
    categories: posCategories,
    stockData,
    getProductStock,
    pagination,
    loading: loading || categoriesLoading,
    error,
    refetch: () => {
      fetchProducts(true);
      fetchInventoryData();
    }
  };
}; 